// The asset page (QEW working page) and the qualifying SOP read. HTML only.
// Mockup: MOCKUP_JSP_APPROVED.html, asset page (approved 2026-10-08). Words: task-pick.ts.
// The live asset page is the baseline: Asset Activity, Authorization Readiness, Select Task,
// Start JSP / Permit at the bottom. Only the Tier 1, 2, 3 pills are gone (JSP_ADJUSTMENTS item 1).
// Buttons carry data-fa so the Field Access page owns every event.

import type { EewpContext } from "./eewp";
import type { AssetGate } from "./field-access";
import { cancelConfirmText, type JspAssetRecord, type JspDraft } from "./jsp";
import type { JspPill } from "./jsp-screens";
import type { TaskAuthorization, TaskDoc } from "./my-tasks";
import {
  activityCells,
  assetLine,
  initialsOf,
  pickGroups,
  taskCardOf,
  PICK_FIRST_LINE,
  SOP_READ_LINE,
  START_LABEL,
  START_LOCKED_LINE,
  type AssetActivity,
  type PickSop,
} from "./task-pick";

/** This QEW's JSP draft on this asset (QEW Page Return Path, approved 2026-10-09). One per QEW per asset. */
export interface OpenDraft {
  jspNumber: string;
  taskId: string;
  taskName: string;
  /** "1:42 PM" in site time. */
  startedTime: string;
  draft: JspDraft | null;
  /** EEWP asked from this draft: Waiting, Withdrawn and so on. Null when none (EEWP_WORDS.md rev 2). */
  eewpStatus?: string | null;
}

/** What loadAssetWork returns. Same shape as functions/src/task-pick.ts. */
export interface AssetWork {
  /** Stage 4b: set when he may not work on this asset. The page shows Not Yet Qualified. */
  gate?: AssetGate | null;
  asset: { assetId: string; isDemo: boolean; equipmentType: string; voltage: string; area: string; siteName: string };
  qew: { badgeId: string; holderName: string; siteName: string; tone: "green" | "yellow"; status: string };
  tasks: TaskDoc[];
  sops: PickSop[];
  taskAuthorizations: TaskAuthorization[];
  taskAuthCycleDays: number | null;
  activity: AssetActivity;
  today: string;
  /** What the JSP's verdict reads from the asset record. */
  engine: JspAssetRecord;
  /** Gloves and training pills on the JSP's black bar. */
  pills: JspPill[];
  /** This QEW's JSP draft on this asset, or null. */
  draft: OpenDraft | null;
  /** Item 5: his submitted JSP on this asset that is open, or that the app closed and still waits for his answer. */
  openJsp?: SubmittedJsp | null;
  /** Item 5: his open JSP at another asset. Submitting here closes it. */
  otherOpen?: { jspNumber: string; assetId: string } | null;
  /** Work Requests section 5: "Open work request WR-0014 · High. ..." for this asset, or null. */
  openRequestLine?: string | null;
  /** EEWP at this asset: EEWP On, cover rule, approvals required, Short. Null on a demo asset or with EEWP off. */
  eewp?: EewpContext | null;
}

/** A submitted JSP the QEW page can reopen at the close pill (JSP_CLOSE_NET_WORDS.md). */
export interface SubmittedJsp {
  jspNumber: string;
  taskId: string;
  taskName: string;
  /** "7:15 AM", or "10/08 7:15 AM" on another day. */
  submittedTime: string;
  draft: JspDraft | null;
  /** Set when the app already closed it. */
  autoAt: string | null;
}

/** The page state the screens read. */
export interface WorkState {
  work: AssetWork;
  taskId: string;
  /** Tasks with a finished practice read on this demo asset, this visit. */
  practiced: ReadonlySet<string>;
  /** Shown above the start button. */
  note: string;
  /** Start was tapped on another task while a draft is open: ask Discard first. */
  confirmDiscard?: boolean;
  /** Cancel JSP #n was tapped under Resume: ask once (MOCKUP_JSP_CANCEL.html, approved 2026-10-14). */
  confirmCancel?: boolean;
}


function esc(value: unknown): string {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function sopFor(work: AssetWork, task: TaskDoc | undefined): PickSop | null {
  if (!task?.sopNumber) return null;
  return work.sops.find((s) => s.sopNumber === task.sopNumber) ?? null;
}

function headHtml(w: AssetWork, button: string): string {
  return `<div class="fa-navh aw-head"><div><div class="e">${esc(w.qew.holderName.toUpperCase())} · ${esc(w.qew.badgeId)}</div>
      <div class="t mono">${esc(w.asset.assetId)}</div>
      <div class="aw-sub">${esc(assetLine(w.asset))}</div></div>${button}</div>`;
}

export function workLoadingHtml(assetId: string, error: string): string {
  return `<div class="fa-navh"><div><div class="e">QEW PAGE</div><div class="t mono">${esc(assetId)}</div></div>
      <button class="bk" type="button" data-fa="dash">My Dashboard</button></div>
    <div class="fa-body">${
      error
        ? `<div class="fa-note red">${esc(error)}</div><button class="fa-go" type="button" data-fa="reload-work">Try Again</button>`
        : '<div class="fa-wait">Loading the asset…</div>'
    }</div>`;
}

/** The line under Resume. */
export function resumeLine(d: OpenDraft): string {
  return `JSP #${d.jspNumber.replace("JSP-", "")} in progress. Started ${d.startedTime}. Picks up where you left off.`;
}

/**
 * Not Yet Qualified (Stage 4b, MOCKUP_SIGNIN_PASS.html, approved 2026-10-10). The locked state first, in amber
 * with a lock; Still needed lists only what is missing; what is done is one grey line. No green on this screen.
 * His name and ID stay in the header so a wrong tag shows.
 */
export function gateHtml(w: AssetWork, gate: AssetGate): string {
  const lock = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="7" width="10" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/></svg>';
  const needs = gate.needs.map((n) => `<div class="aw-need"><span class="dot" aria-hidden="true"></span><div><b>${esc(n.title)}</b><span>${esc(n.line)}</span></div></div>`).join("");
  return `${headHtml(w, "")}
    <div class="fa-body">
      <div class="aw-gate" role="status"><div class="gh"><span class="lk">${lock}</span><div><b>Not Yet Qualified For This Equipment</b><span>${esc(gate.sub)}</span></div></div>
        <div class="sec">Still needed</div>${needs}
        ${gate.done ? `<div class="donel">${esc(gate.done)}</div>` : ""}</div>
      <button class="fa-go" type="button" data-fa="dash">My Dashboard</button>
      <button class="fa-plain" type="button" data-fa="guest">Guest Page</button>
    </div>`;
}

export function workHtml(s: WorkState): string {
  const w = s.work;
  if (w.gate) return gateHtml(w, w.gate);
  const cells = activityCells(w.activity)
    .map((c) => `<div><b${c.amber ? ' class="am"' : ""}>${esc(c.value)}</b><span>${esc(c.label)}</span></div>`)
    .join("");
  const groups = pickGroups(w.tasks)
    .map(
      (g) =>
        `<optgroup label="${esc(g.label)}">${g.tasks.map((t) => `<option value="${esc(t.taskId)}"${t.taskId === s.taskId ? " selected" : ""}>${esc(t.name)}</option>`).join("")}</optgroup>`,
    )
    .join("");
  const task = w.tasks.find((t) => t.taskId === s.taskId && t.isActive);
  const card = cardFor(w, s.taskId, s.practiced);
  const cardHtml = card
    ? `<div class="aw-tcard ${card.tone}"><div class="tn">${esc(card.name)}</div><div class="tm">${esc(card.meta)}</div>
        <div class="ts">${esc(card.line)}${card.sub ? `<small>${esc(card.sub)}</small>` : ""}</div>
        ${
          !card.review
            ? ""
            : card.reviewSmall
              ? `<button class="fa-link aw-revl" type="button" data-fa="review">${esc(card.review)}</button>`
              : `<button class="${card.review === "Review SOP" ? "fa-go" : "fa-plain"} aw-rev" type="button" data-fa="review">${esc(card.review)}</button>`
        }</div>`
    : "";
  const d = w.draft;
  const n = d ? d.jspNumber.replace("JSP-", "") : "";
  const resume = Boolean(d && task && d.taskId === task.taskId);
  const open = resume || Boolean(card?.startOpen);
  const under = resume ? resumeLine(d!) : !card ? PICK_FIRST_LINE : open ? "" : card.state === "noSop" ? "" : START_LOCKED_LINE;
  // Item 5 (JSP_CLOSE_NET_WORDS.md): an open JSP here takes the main button, same spot as Resume.
  // An open JSP at another asset gets one grey line: submitting here closes it. Nothing stops him.
  const oj = w.openJsp && !w.openJsp.autoAt ? w.openJsp : null;
  const other = !oj && w.otherOpen ? `JSP #${w.otherOpen.jspNumber.replace("JSP-", "")} at ${w.otherOpen.assetId} is still open. Submitting this one closes it.` : "";
  const startHtml = oj
    ? `<button class="aw-start" type="button" data-fa="closejsp">Close JSP #${esc(oj.jspNumber.replace("JSP-", ""))}</button>
      <div class="aw-startn">${esc(`Submitted ${oj.submittedTime}. Close it when the work is done.`)}</div>`
    : d && resume && s.confirmCancel
      ? (() => {
          const c = cancelConfirmText(d.jspNumber, d.taskName, d.startedTime);
          return `<div class="fa-note grey aw-cfm"><b>${esc(c.title)}</b><span>${esc(c.body)}</span>
          <div class="row"><button class="fa-plain" type="button" data-fa="keepjsp">Keep Working</button><button class="fa-go aw-cancel-go" type="button" data-fa="cancelconfirm">Cancel JSP</button></div></div>`;
        })()
    : d && !resume && s.confirmDiscard && task
      ? `<div class="fa-note grey aw-cfm"><b>Discard JSP #${esc(n)}?</b><span>JSP #${esc(n)} is a ${esc(d.taskName)} you started at ${esc(d.startedTime)}. Discarding it keeps it on record as discarded. Then ${esc(task.name)} starts a new JSP.</span>
          <div class="row"><button class="fa-plain" type="button" data-fa="keepdraft">Keep JSP #${esc(n)}</button><button class="fa-go" type="button" data-fa="discardstart">Discard And Start</button></div></div>`
      : `<button class="aw-start${open ? "" : " off"}" type="button" data-fa="start"${open ? "" : " disabled"}>${resume ? `Resume JSP #${esc(n)}` : START_LABEL}</button>
      ${under ? `<div class="aw-startn">${esc(under)}</div>` : ""}${other ? `<div class="aw-startn">${esc(other)}</div>` : ""}
      ${resume ? `<button class="fa-link aw-cancel" type="button" data-fa="canceljsp">Cancel JSP #${esc(n)}</button>` : ""}`;
  return `${headHtml(w, '<button class="bk" type="button" data-fa="dash">My Dashboard</button>')}
    <div class="fa-body">
      <div class="aw-card"><div class="aw-hy">Asset Activity</div><div class="aw-act">${cells}</div></div>
      <div class="aw-card"><div class="aw-hd">Authorization Readiness</div>
        <div class="aw-who"><div class="av">${esc(initialsOf(w.qew.holderName))}</div>
          <div class="wn"><b>${esc(w.qew.holderName)}</b><span>${esc(w.qew.siteName)}</span></div>
          <div class="qa ${w.qew.tone}"><i></i>${esc(w.qew.status)}</div></div></div>
      ${w.openRequestLine ? `<div class="fa-note aw-wr" role="status">${esc(w.openRequestLine)}</div>` : ""}
      <div class="aw-card"><div class="aw-hm">Select Task</div><div class="aw-pick">
        <select id="aw-task" class="aw-sel" aria-label="Task"><option value=""${s.taskId ? "" : " selected"}>Pick a task</option>${groups}</select>
        ${cardHtml}</div></div>
      ${s.note ? `<div class="fa-note grey">${esc(s.note)}</div>` : ""}
      <div style="flex:1"></div>
      ${startHtml}
      <button class="fa-link aw-guest" type="button" data-fa="guest">Guest Page</button>
    </div>`;
}

/** The card for the picked task, or null. */
export function cardFor(w: AssetWork, taskId: string, practiced: ReadonlySet<string>) {
  const task = w.tasks.find((t) => t.taskId === taskId && t.isActive);
  return task ? taskCardOf(task, w.taskAuthorizations.find((a) => a.taskId === task.taskId), sopFor(w, task), w.taskAuthCycleDays, w.today, w.asset.isDemo, practiced.has(task.taskId)) : null;
}

/** Review SOP once qualified, or after a practice read, is a reference read: no timer, no checkpoints, no acknowledgment. */
export const SOP_REFERENCE_LINE = "Reference read. You are already qualified on this task.";
export const SOP_REFERENCE_PRACTICE_LINE = "Reference read. Practice read already done.";

export interface SopReadState {
  work: AssetWork;
  task: TaskDoc;
  sop: PickSop;
  frameUrl: string | null;
  /** Set while the acknowledgment is being recorded. */
  busy: boolean;
  /** Already qualified or practiced: the SOP opens for reference only. */
  reference: boolean;
  error: string;
}

export function sopReadHtml(s: SopReadState): string {
  const eyebrow = `${s.sop.sopNumber}${s.sop.version ? ` · REV ${s.sop.version}` : ""}`;
  const body = s.error
    ? `<div class="fa-body"><div class="fa-note red">${esc(s.error)}</div>
        <button class="fa-go" type="button" data-fa="ack-retry">Try Again</button>
        <button class="fa-plain" type="button" data-fa="sopread-cancel">Cancel</button></div>`
    : s.busy
      ? '<div class="fa-body"><div class="fa-wait">Recording your SOP review…</div></div>'
      : s.frameUrl
        ? `<div class="res-review">${esc(s.reference ? (s.work.asset.isDemo ? SOP_REFERENCE_PRACTICE_LINE : SOP_REFERENCE_LINE) : s.work.asset.isDemo ? `${SOP_READ_LINE} Practice only on a demo asset.` : SOP_READ_LINE)}</div>
          <iframe class="res-frame" id="aw-sop" title="${esc(s.sop.sopNumber)}" src="${esc(s.frameUrl)}"></iframe>`
        : '<div class="fa-body"><div class="fa-wait">Opening the SOP…</div></div>';
  return `<div class="fa-navh"><div><div class="e">${esc(eyebrow)}</div><div class="t">${esc(s.task.name)}</div></div>
      <button class="bk" type="button" data-fa="sopread-cancel"${s.busy ? " disabled" : ""}>${s.reference ? "Back" : "Cancel"}</button></div>${body}`;
}
